import GenericRadioConfig from 'components/Configs/GenericRadioConfig';
import SectionHeader from 'components/Basic/SectionHeader/SectionHeader';
import { PlayerHandedness, PitchTagging, StrikeZoneDisplay, UmpireCallCorrectness, UmpireCall } from 'types/CodegenReportTypes';
import { useReportId } from 'hooks/helperHooks';
import useUmpireAllSessionsReport from 'hooks/UmpireReport/AllSessions/useUmpireAllSessionsReport';
import { useTranslation } from 'react-i18next';
import SeePitchesBy from 'components/Configs/SeePitchesBy';

type Props = {
  isConfigOpen: boolean;
  setConfigOpen: () => void;
};

const AllSessionsConfig = ({ isConfigOpen, setConfigOpen }: Props) => {
  const { isCreate } = useReportId();

  const { specialConfigOptions, selectedConfgValues, setSelectedConfigValue, isLoadingReport } = useUmpireAllSessionsReport();

  const { t } = useTranslation();

  return (
    <>
      <SectionHeader isOpen={isConfigOpen} onClick={setConfigOpen} title={t('Adjust report content')} isEnabled={isCreate} />
      {isConfigOpen && (
        <div className='report-layout-shelf-section'>
          <GenericRadioConfig
            label={t('Zone view:')}
            options={[
              {
                value: 'WithBuffers' satisfies StrikeZoneDisplay,
                subOptions: {
                  type: 'Checkbox',
                  options: ['Correct', 'Incorrect', 'BufferZone'] satisfies UmpireCallCorrectness[],
                  selected: selectedConfgValues.includedCallCorrectness,
                  setSelected: (newValue) => setSelectedConfigValue('includedCallCorrectness', newValue as UmpireCallCorrectness[]),
                },
              },
              {
                value: 'WithoutBuffers' satisfies StrikeZoneDisplay,
                subOptions: {
                  type: 'Radio',
                  options: ['All', 'Correct', 'Incorrect'] satisfies ('All' | UmpireCallCorrectness)[],
                  selected:
                    selectedConfgValues.includedCallCorrectness.length === 2 ? ['All'] : selectedConfgValues.includedCallCorrectness,
                  setSelected: (newValue) =>
                    setSelectedConfigValue(
                      'includedCallCorrectness',
                      newValue[0] === 'All' ? ['Correct', 'Incorrect'] : (newValue as UmpireCallCorrectness[])
                    ),
                },
              },
            ]}
            selected={selectedConfgValues.strikeZoneDisplay}
            setSelected={(newValue) => {
              setSelectedConfigValue('strikeZoneDisplay', newValue);
              setSelectedConfigValue(
                'includedCallCorrectness',
                newValue === 'WithBuffers' ? ['Correct', 'Incorrect', 'BufferZone'] : ['Correct', 'Incorrect']
              );
            }}
            disabled={isLoadingReport}
          />
          <GenericRadioConfig
            label={t('Umpire call:')}
            options={['All', 'BallCalled', 'StrikeCalled'] satisfies UmpireCall[]}
            selected={selectedConfgValues.includedCalls}
            setSelected={(newValue) => setSelectedConfigValue('includedCalls', newValue)}
            disabled={isLoadingReport}
          />{' '}
          <GenericRadioConfig
            label={t('Pitch tagging:')}
            options={['Auto', 'Manual'] satisfies PitchTagging[]}
            selected={selectedConfgValues.pitchTagging}
            setSelected={(newValue) => setSelectedConfigValue('pitchTagging', newValue)}
            disabled={isLoadingReport}
          />
          <GenericRadioConfig
            label={t('Batter handedness:')}
            options={['Both', 'Left', 'Right'] satisfies PlayerHandedness[]}
            selected={selectedConfgValues.batterHandedness}
            setSelected={(newValue) => setSelectedConfigValue('batterHandedness', newValue)}
            disabled={isLoadingReport}
          />
          <GenericRadioConfig
            label={t('Pitcher handedness:')}
            options={['Both', 'Left', 'Right'] satisfies PlayerHandedness[]}
            selected={selectedConfgValues.pitcherHandedness}
            setSelected={(newValue) => setSelectedConfigValue('pitcherHandedness', newValue)}
            disabled={isLoadingReport}
          />
          <SeePitchesBy
            seePitchesBy={selectedConfgValues.pitchGrouping}
            setSeePitchesBy={(newValue) => setSelectedConfigValue('pitchGrouping', newValue)}
            pitchTypes={selectedConfgValues.pitchTypes}
            setPitchTypes={(newValue) => setSelectedConfigValue('pitchTypes', newValue)}
            pitchTypeGroups={selectedConfgValues.pitchTypeGroups}
            setPitchTypeGroups={(newValue) => setSelectedConfigValue('pitchTypeGroups', newValue)}
            disabled={isLoadingReport}
            availablePitchTypes={
              selectedConfgValues.pitchTagging === 'Auto'
                ? specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypes
                : specialConfigOptions?.pitchTypeInfo.availableManualPitchTypes
            }
            availablePitchTypeGroups={
              selectedConfgValues.pitchTagging === 'Auto'
                ? specialConfigOptions?.pitchTypeInfo.availableAutoPitchTypeGroups
                : specialConfigOptions?.pitchTypeInfo.availableManualPitchTypeGroups
            }
          />
        </div>
      )}
    </>
  );
};

export default AllSessionsConfig;
